import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Upload, Loader2, Star, Trash2, ImageOff, GripVertical } from 'lucide-react';
import { SectionHeader, ActionButton } from './VisualAtoms';
import {
  addArtworkImage,
  deleteArtworkImage,
  directUploadToSupabase,
  listArtworkImages,
  requestArtworkUploadUrl,
  reorderArtworkImages,
  updateArtworkImage,
} from '../lib/artworksApi';

/**
 * Admin gallery section for multi-image support on a published artwork.
 * Renders inside the full-screen ArtworkEditorPage. Only shown once the
 * artwork has been created (an id must exist).
 */
export default function ArtworkImagesSection({ artworkId, onPrimaryChanged }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const fileRef = useRef(null);
  const dragging = useRef(null);

  const refresh = () => {
    setLoading(true);
    listArtworkImages(artworkId, 'gallery')
      .then((data) => setImages(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [artworkId]);

  const doUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 30 * 1024 * 1024) {
          toast.error(`${file.name}: too large (max 30MB)`);
          continue;
        }
        const signed = await requestArtworkUploadUrl({ file_name: file.name, mime_type: file.type, kind: 'media' });
        await directUploadToSupabase(signed.signed_url, file);
        await addArtworkImage(artworkId, {
          image_url: signed.public_url,
          storage_path: signed.path,
          bucket_name: signed.bucket_name,
          alt_text: file.name.replace(/\.[^.]+$/, ''),
          section: 'gallery',
        });
      }
      toast.success(files.length > 1 ? `Uploaded ${files.length} images` : 'Image uploaded');
      refresh();
      onPrimaryChanged && onPrimaryChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const setPrimary = async (imageId) => {
    setBusyId(imageId);
    try {
      await updateArtworkImage(artworkId, imageId, { is_primary: true });
      toast.success('Primary image updated');
      refresh();
      onPrimaryChanged && onPrimaryChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const removeImage = async (imageId) => {
    setBusyId(imageId);
    try {
      await deleteArtworkImage(artworkId, imageId);
      toast.success('Image removed');
      refresh();
      onPrimaryChanged && onPrimaryChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const onDragStart = (id) => (e) => {
    dragging.current = id;
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = (targetId) => async (e) => {
    e.preventDefault();
    const draggedId = dragging.current;
    dragging.current = null;
    if (!draggedId || draggedId === targetId) return;
    const ordered = [...images];
    const from = ordered.findIndex((im) => im.id === draggedId);
    const to = ordered.findIndex((im) => im.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = ordered.splice(from, 1);
    ordered.splice(to, 0, moved);
    setImages(ordered);
    try {
      await reorderArtworkImages(artworkId, ordered.map((im) => im.id));
      onPrimaryChanged && onPrimaryChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reorder failed');
      refresh();
    }
  };

  return (
    <section className="border border-[#1a1a1a] bg-[#0a0a0a] p-6" data-testid="artwork-images-section">
      <SectionHeader
        title="Artwork gallery"
        helper="Add more images beyond the cover. Drag to reorder. Click the star to set the primary/hero image. Additional images appear as a slider on the public page."
      />

      <div className="flex items-center gap-3 mb-5">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          data-testid="artwork-images-file-input"
          onChange={(e) => doUpload(Array.from(e.target.files || []))}
        />
        <ActionButton
          data-testid="artwork-images-upload-btn"
          variant="secondary"
          icon={uploading ? Loader2 : Upload}
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? 'Uploading…' : 'Add images'}
        </ActionButton>
        {images.length > 0 && (
          <span className="text-[10px] font-mono text-[#666] uppercase tracking-[0.14em]">
            {images.length} image{images.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[11px] font-mono text-[#666]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : images.length === 0 ? (
        <div className="border border-dashed border-[#1a1a1a] p-8 text-center">
          <ImageOff className="w-6 h-6 text-[#333] mx-auto" />
          <p className="mt-3 text-[11px] font-mono text-[#666] uppercase tracking-[0.14em]">
            No images yet
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <AnimatePresence initial={false}>
            {images.map((im) => (
              <motion.div
                key={im.id}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                draggable
                onDragStart={onDragStart(im.id)}
                onDragOver={onDragOver}
                onDrop={onDrop(im.id)}
                data-testid={`artwork-image-tile-${im.id}`}
                className={`relative aspect-square border overflow-hidden bg-[#050505] group cursor-grab active:cursor-grabbing ${im.is_primary ? 'border-[#90E0EF]/50' : 'border-[#1a1a1a]'}`}
              >
                <img src={im.image_url} alt="" className="w-full h-full object-cover" />

                {im.is_primary && (
                  <div className="absolute top-2 left-2 bg-[#050505]/85 border border-[#90E0EF]/50 backdrop-blur-sm px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.16em] text-[#90E0EF]">
                    Primary
                  </div>
                )}

                <div className="absolute top-2 right-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <GripVertical className="w-4 h-4 text-white/60 mx-auto" />
                </div>

                <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between p-2 bg-gradient-to-t from-[#050505]/95 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  {!im.is_primary ? (
                    <button
                      type="button"
                      onClick={() => setPrimary(im.id)}
                      disabled={busyId === im.id}
                      data-testid={`artwork-image-primary-${im.id}`}
                      className="p-1.5 border border-[#1a1a1a] bg-[#050505]/90 hover:border-[#90E0EF]/50 text-[#90E0EF] transition-colors disabled:opacity-40"
                      title="Set as primary"
                    >
                      {busyId === im.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />}
                    </button>
                  ) : <span />}
                  <button
                    type="button"
                    onClick={() => removeImage(im.id)}
                    disabled={busyId === im.id}
                    data-testid={`artwork-image-delete-${im.id}`}
                    className="p-1.5 border border-[#1a1a1a] bg-[#050505]/90 hover:border-[#e28f8f] text-[#e28f8f] transition-colors disabled:opacity-40"
                    title="Remove"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </section>
  );
}
